import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { formatDate, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar, 
  CheckCircle2, 
  CreditCard, 
  Download, 
  FileText, 
  Loader2, 
  MoreHorizontal, 
  RefreshCw, 
  Send,
  Clock,
  Receipt,
  Building,
  User,
  CalendarClock,
  AlertCircle,
  CheckCircle,
  XCircle,
  DollarSign,
  Eye,
  ExternalLink,
  TrendingUp,
  Activity,
  Mail,
  Phone,
  MapPin,
  Package,
  Star,
  Copy,
  Share2,
  Hash,
  MapPinIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "@/components/common/PageHeader";
import { StatusBadge } from '@/components/common/StatusBadge';

interface InvoiceDetail {
  id: string;
  subscription_id: string;
  customer_id: string;
  customer_name: string;
  customer_type: string;
  legal_name: string;
  business_identification_number: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  invoice_number: string;
  invoice_date: string;
  amount_due: number;
  currency: string;
  due_date: string | null;
  paid_date: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  items: {
    id: string;
    amount_due: number;
    subscription_item: {
      subscription_id: string;
      subscription_plan: {
        name: string;
      };
      quantity: number;
      unit_price: number;
    };
  }[];
}

const InvoiceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMarkingAsPaid, setIsMarkingAsPaid] = useState(false);
  const [isMarkingAsPaidDialogOpen, setIsMarkingAsPaidDialogOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [paymentReference, setPaymentReference] = useState('');

  useEffect(() => {
    if (id) {
      fetchInvoiceDetails(id);
    } else {
      setLoading(false);
      toast.error('Número da fatura não fornecido');
    }
  }, [id]);

  const fetchInvoiceDetails = async (invoiceId: string) => {
    setLoading(true);
    try {
      console.log('Buscando detalhes da fatura:', invoiceId);
      
      // Buscar a fatura principal da nova view
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('invoices_')
        .select(`
          id,
          subscription_id,
          customer_id,
          customer_name,
          customer_type,
          legal_name,
          business_identification_number,
          address,
          address2,
          city,
          state,
          zip,
          invoice_number,
          invoice_date,
          amount_due,
          currency,
          due_date,
          paid_date,
          status,
          created_at,
          updated_at,
          created_by,
          updated_by
        `)
        .eq('id', invoiceId)
        .single();
      
      if (invoiceError) {
        console.error('Erro ao buscar fatura:', invoiceError);
        throw invoiceError;
      }
      
      if (!invoiceData) {
        console.error('Fatura não encontrada:', invoiceId);
        throw new Error('Fatura não encontrada');
      }
      
      console.log('Fatura encontrada:', invoiceData);
      
      // Buscar os itens da fatura
      const { data: invoiceItems, error: invoiceItemsError } = await supabase
        .from('invoice_items')
        .select(`
          id,
          quantity,
          unit_price,
          amount_price,
          subscription_plan_id,
          subscription_plan:subscription_plan_id (
            id,
            name
          )
        `)
        .eq('invoice_id', invoiceData.id);
      
      if (invoiceItemsError) {
        console.error('Erro ao buscar itens da fatura:', invoiceItemsError);
        throw invoiceItemsError;
      }
      
      console.log('Itens da fatura encontrados:', invoiceItems);
      
      // Construir o objeto de detalhes da fatura usando os dados da nova view
      const invoiceDetail: InvoiceDetail = {
        id: invoiceData.id,
        subscription_id: invoiceData.subscription_id,
        customer_id: invoiceData.customer_id,
        customer_name: invoiceData.customer_name,
        customer_type: invoiceData.customer_type,
        legal_name: invoiceData.legal_name,
        business_identification_number: invoiceData.business_identification_number,
        address: invoiceData.address,
        address2: invoiceData.address2,
        city: invoiceData.city,
        state: invoiceData.state,
        zip: invoiceData.zip,
        invoice_number: invoiceData.invoice_number,
        invoice_date: invoiceData.invoice_date,
        amount_due: invoiceData.amount_due,
        currency: invoiceData.currency,
        due_date: invoiceData.due_date,
        paid_date: invoiceData.paid_date,
        status: invoiceData.status,
        created_at: invoiceData.created_at,
        updated_at: invoiceData.updated_at,
        created_by: invoiceData.created_by,
        updated_by: invoiceData.updated_by,
        items: invoiceItems.map(item => ({
          id: item.id,
          amount_due: item.amount_price || 0,
          subscription_item: {
            subscription_id: invoiceData.subscription_id,
            subscription_plan: {
              name: (item.subscription_plan as any)?.name || 'Plano sem nome'
            },
            quantity: item.quantity || 0,
            unit_price: item.unit_price || 0
          }
        }))
      };
      
      setInvoice(invoiceDetail);
    } catch (error) {
      console.error('Erro ao buscar detalhes da fatura:', error);
      toast.error('Não foi possível carregar os detalhes da fatura');
    } finally {
      setLoading(false);
    }
  };

  const resetPaymentForm = () => {
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentReference('');
    setIsMarkingAsPaid(false);
    setIsClosing(false);
  };

  const handleClosePaymentDialog = () => {
    if (!isMarkingAsPaid && !isClosing) {
      setIsClosing(true);
      resetPaymentForm();
      setIsMarkingAsPaidDialogOpen(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!invoice || isMarkingAsPaid || isClosing) return;
    
    setIsMarkingAsPaid(true);
    
    try {
      // Atualizar a fatura principal para status "paid"
      const { error } = await supabase
        .from('invoices')
        .update({
          status: 'paid',
          paid_date: new Date(paymentDate).toISOString(),
          updated_by: 'user'
        })
        .eq('invoice_number', invoice.invoice_number);
      
      if (error) {
        throw error;
      }
      
      // Registrar a operação na tabela de orders
      const { error: orderError } = await supabase
        .from('orders')
        .insert({
          subscription_id: invoice.subscription_id,
          operation_type: 'pagamento',
          quantity_change: null,
          operation_date: new Date().toISOString(),
        });
      
      if (orderError) {
        throw orderError;
      }
      
      toast.success('Fatura marcada como paga com sucesso');
      
      // Mark as closing before cleanup
      setIsClosing(true);
      
      // Close dialog with delay to ensure smooth transition
      setTimeout(() => {
        setIsMarkingAsPaidDialogOpen(false);
        resetPaymentForm();
        // Atualizar os dados da fatura
        fetchInvoiceDetails(invoice.id);
      }, 100);
      
    } catch (error) {
      console.error('Erro ao marcar fatura como paga:', error);
      toast.error('Não foi possível marcar a fatura como paga');
      setIsMarkingAsPaid(false);
    }
  };

  const handleCancelInvoice = async () => {
    if (!invoice) return;
    
    if (!confirm('Tem certeza que deseja cancelar esta fatura? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    try {
      // Atualizar a fatura principal para status "canceled"
      const { error } = await supabase
        .from('invoices')
        .update({
          status: 'canceled',
          updated_by: 'user'
        })
        .eq('invoice_number', invoice.invoice_number);
      
      if (error) {
        throw error;
      }
      
      // Registrar a operação na tabela de orders
      const { error: orderError } = await supabase
        .from('orders')
        .insert({
          subscription_id: invoice.subscription_id,
          operation_type: 'cancelamento_fatura',
          quantity_change: null,
          operation_date: new Date().toISOString(),
        });
      
      if (orderError) {
        throw orderError;
      }
      
      toast.success('Fatura cancelada com sucesso');
      
      // Atualizar os dados da fatura
      await fetchInvoiceDetails(invoice.id);
    } catch (error) {
      console.error('Erro ao cancelar fatura:', error);
      toast.error('Não foi possível cancelar a fatura');
    }
  };

  const handleSendInvoice = () => {
    toast.info('Funcionalidade de envio de fatura será implementada em breve');
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  const handleDownloadInvoice = () => {
    toast.info('Funcionalidade de download de fatura será implementada em breve');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Gradient Background */}
        <div className="relative bg-gradient-to-br from-[#3b65f5] via-[#ff4a8f] to-[#c8e600] overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center text-center text-white">
              <Loader2 className="h-16 w-16 animate-spin mb-6" />
              <h1 className="text-2xl font-extrabold mb-2">Carregando Fatura</h1>
              <p className="text-white/80">Aguarde enquanto buscamos os detalhes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Gradient Background */}
        <div className="relative bg-gradient-to-br from-red-500 via-red-600 to-red-700 overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center text-center text-white">
              <XCircle className="h-16 w-16 mb-6" />
              <h1 className="text-2xl font-extrabold mb-2">Fatura Não Encontrada</h1>
              <p className="text-white/80 mb-6">A fatura solicitada não foi encontrada em nossos registros.</p>
              <Button 
                variant="secondary" 
                onClick={() => navigate(-1)}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getStatusGradient = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'from-green-500 via-green-600 to-green-700';
      case 'pending':
        return 'from-yellow-500 via-yellow-600 to-yellow-700';
      case 'overdue':
        return 'from-orange-500 via-orange-600 to-orange-700';
      case 'canceled':
        return 'from-red-500 via-red-600 to-red-700';
      default:
        return 'from-[#3b65f5] via-[#ff4a8f] to-[#c8e600]';
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      {/* Hero Section with Dynamic Gradient Based on Status */}
      <div className={`relative bg-gradient-to-br ${getStatusGradient(invoice.status)} overflow-hidden`}>
        <div className="absolute inset-0 bg-black/20" />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full transform translate-x-48 -translate-y-48" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full transform -translate-x-32 translate-y-32" />
        
        <div className="relative z-10 container mx-auto px-6 py-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-white">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate(-1)}
                  className="text-white hover:bg-white/20 border-white/30"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="h-8 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <Receipt className="h-6 w-6" />
                  <span className="text-white/80">Fatura</span>
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-extrabold mb-3">
                #{invoice.invoice_number}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-white/90">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  <span>{invoice.customer_name}</span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <span className="font-bold">{formatCurrency(invoice.amount_due, invoice.currency)}</span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <StatusBadge 
                  status={invoice.status} 
                  context="invoice" 
                  className="bg-white/20 text-white border-white/30"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                  >
                    <MoreHorizontal className="h-4 w-4 mr-2" />
                    Ações
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Gerenciar Fatura</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {invoice.status.toLowerCase() === 'pending' && (
                    <DropdownMenuItem onClick={() => setIsMarkingAsPaidDialogOpen(true)}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Marcar como Paga
                    </DropdownMenuItem>
                  )}
                  {invoice.status.toLowerCase() === 'pending' && (
                    <DropdownMenuItem onClick={handleCancelInvoice} className="text-red-600">
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancelar Fatura
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSendInvoice}>
                    <Send className="h-4 w-4 mr-2" />
                    Enviar por E-mail
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handlePrintInvoice}>
                    <Eye className="h-4 w-4 mr-2" />
                    Visualizar/Imprimir
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDownloadInvoice}>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigator.clipboard.writeText(window.location.href)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copiar Link
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button 
                onClick={() => navigate(`/subscriptions/${invoice.subscription_id}`)}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Ver Assinatura
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10 space-y-8 pb-8">
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Status Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-md">
                    <Activity className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Status</span>
                </div>
                <StatusBadge status={invoice.status} context="invoice" />
              </div>
              {invoice.paid_date && (
                <div className="text-xs text-gray-500">
                  Pago em {formatDate(invoice.paid_date)}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Amount Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded-md">
                    <DollarSign className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Valor Total</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {formatCurrency(invoice.amount_due, invoice.currency)}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {invoice.items.length} item{invoice.items.length !== 1 ? 's' : ''}
              </div>
            </CardContent>
          </Card>

          {/* Issue Date Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-orange-100 rounded-md">
                    <Calendar className="h-4 w-4 text-orange-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Emissão</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-extrabold text-gray-900">
                    {formatDate(invoice.invoice_date)}
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Data de criação
              </div>
            </CardContent>
          </Card>

          {/* Due Date Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-yellow-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-yellow-100 rounded-md">
                    <Clock className="h-4 w-4 text-yellow-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Vencimento</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-extrabold text-gray-900">
                    {invoice.due_date ? formatDate(invoice.due_date) : 'N/A'}
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {invoice.due_date ? 'Data limite' : 'Sem vencimento'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Customer Information */}
          <Card className="lg:col-span-1 border shadow-sm bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5 text-blue-600" />
                Informações do Cliente
              </CardTitle>
              <CardDescription>
                Detalhes da empresa e assinatura
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-sm font-extrabold text-gray-600 mb-2 flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Cliente
                </h3>
                <div className="space-y-1">
                  <div className="font-extrabold text-gray-900">
                    {invoice.customer_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {invoice.legal_name}
                  </div>
                  {invoice.business_identification_number && (
                    <div className="text-sm text-gray-500 font-mono flex items-center gap-1">
                      <Hash className="h-3 w-3" />
                      {invoice.business_identification_number}
                    </div>
                  )}
                </div>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="text-sm font-extrabold text-gray-600 mb-2 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Tipo de Cliente
                </h3>
                <div className="text-sm text-gray-900">
                  {invoice.customer_type === 'person' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                </div>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="text-sm font-extrabold text-gray-600 mb-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Endereço
                </h3>
                <div className="space-y-1 text-sm text-gray-900">
                  <div>{invoice.address}</div>
                  {invoice.address2 && <div>{invoice.address2}</div>}
                  <div>{invoice.city}, {invoice.state} {invoice.zip}</div>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="text-sm font-extrabold text-gray-600 mb-2 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Assinatura
                </h3>
                <div className="space-y-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/subscriptions/${invoice.subscription_id}`)}
                    className="w-full"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Ver Assinatura
                  </Button>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="text-sm font-extrabold text-gray-600 mb-3 flex items-center gap-2">
                  <CalendarClock className="h-4 w-4" />
                  Cronologia
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Emissão</span>
                    <span className="text-sm font-extrabold">{formatDate(invoice.invoice_date)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Vencimento</span>
                    <span className="text-sm font-extrabold">
                      {invoice.due_date ? formatDate(invoice.due_date) : 'N/A'}
                    </span>
                  </div>
                  
                  {invoice.paid_date && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Pagamento</span>
                      <span className="text-sm font-extrabold text-green-600">
                        {formatDate(invoice.paid_date)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Items */}
          <Card className="lg:col-span-2 border shadow-sm bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-green-600" />
                Itens da Fatura
              </CardTitle>
              <CardDescription>
                Detalhamento dos produtos e serviços cobrados
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Items Table */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-extrabold">Plano/Produto</TableHead>
                        <TableHead className="text-center font-extrabold">Qtd.</TableHead>
                        <TableHead className="font-extrabold">Preço Unit.</TableHead>
                        <TableHead className="text-right font-extrabold">Subtotal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoice.items.map((item, index) => (
                        <TableRow key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                          <TableCell>
                            <div className="font-extrabold text-gray-900">
                              {item.subscription_item.subscription_plan.name}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="font-extrabold">
                              {item.subscription_item.quantity}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-extrabold">
                            {formatCurrency(item.subscription_item.unit_price, invoice.currency)}
                          </TableCell>
                          <TableCell className="text-right font-extrabold">
                            {formatCurrency(item.amount_due, invoice.currency)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Total Section */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-extrabold text-gray-600">Total da Fatura:</span>
                    <span className="text-2xl font-extrabold text-gray-900">
                      {formatCurrency(invoice.amount_due, invoice.currency)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal para marcar como pago */}
      <Dialog 
        open={isMarkingAsPaidDialogOpen} 
        onOpenChange={(open) => {
          if (!open && !isMarkingAsPaid && !isClosing) {
            handleClosePaymentDialog();
          } else if (open && !isClosing) {
            setIsMarkingAsPaidDialogOpen(open);
            setIsClosing(false);
          }
        }}
        modal={true}
      >
        <DialogContent 
          className="sm:max-w-md"
          onPointerDownOutside={(e) => {
            if (isMarkingAsPaid || isClosing) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (isMarkingAsPaid || isClosing) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            if (isMarkingAsPaid || isClosing) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 bg-green-100 rounded-full">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              Marcar Fatura como Paga
            </DialogTitle>
            <DialogDescription>
              Confirme os detalhes do pagamento para atualizar o status desta fatura.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="payment-date" className="text-sm font-extrabold">
                Data de Pagamento *
              </Label>
              <Input 
                id="payment-date" 
                type="date" 
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full"
                disabled={isMarkingAsPaid || isClosing}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="payment-reference" className="text-sm font-extrabold">
                Referência de Pagamento
              </Label>
              <Input 
                id="payment-reference" 
                placeholder="Ex: Transação #12345, PIX, Boleto..."
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                className="w-full"
                disabled={isMarkingAsPaid || isClosing}
              />
              <p className="text-xs text-gray-500">Campo opcional para controle interno</p>
            </div>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-start gap-3">
                <div className="p-1 bg-blue-100 rounded-full">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-extrabold text-blue-800 text-sm">Confirmação de Pagamento</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Esta ação irá atualizar o status da fatura para "Pago" e registrar a data de pagamento. 
                    O histórico da assinatura será atualizado automaticamente.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={handleClosePaymentDialog}
              disabled={isMarkingAsPaid || isClosing}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleMarkAsPaid}
              disabled={isMarkingAsPaid || isClosing || !paymentDate}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isMarkingAsPaid ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Confirmar Pagamento
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceDetail; 